/**
 * GuidedTour — the first-run spotlight walkthrough.
 *
 * The script lives in `lib/tourSteps.ts`; this file is only the engine. For
 * each step it:
 *   1. navigates to the step's route (hashNavigate) if we are not there,
 *   2. waits for the step's `data-testid` element to exist,
 *   3. scrolls it to the middle of the viewport,
 *   4. tracks its rect and paints a spotlight ring around it,
 *   5. places the copy card in whichever half of the screen the ring is not.
 *
 * Design notes
 * ------------
 * The dim is ONE element, not four panels: a zero-background div sized to the
 * target with `box-shadow: 0 0 0 9999px <dim>` punches an exact hole with no
 * seams and no SVG mask. It sits under the card and above the app.
 *
 * The overlay swallows taps on the app underneath on purpose. A tour that lets
 * you half-drive the app ends up in states the next step does not expect (a
 * modal open, a different dex selected, the grid scrolled elsewhere). The tour
 * SHOWS the control and the trainer uses it for real the moment the tour ends.
 *
 * Degradation is the important property here. A target can legitimately be
 * absent: the Dex grid is empty on a brand new account until sprites hydrate,
 * a Pro-gated control may not render, a device may not support PiP. When the
 * element does not turn up within TARGET_WAIT_MS the step falls back to a
 * centred card with the same copy. No step is ever allowed to stall the tour.
 *
 * Theming: every colour is a token, so the tour tracks light and dark like the
 * rest of the shell. Copy rules follow Onboarding.tsx (no emojis, no em dashes).
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X, Check } from "lucide-react";
import { hashGetSnapshot, hashNavigate } from "@/lib/hashRouter";
import { stepForPlatform, type TourStep } from "@/lib/tourSteps";
import { stepsForScript, resolveWalkthroughRoute, resolveWalkthroughTarget } from "@/lib/walkthroughs";
import { getPlatform } from "@/lib/platform";
import { setGuidedTourScrollActive } from "@/lib/guidedTourScroll";
import { setTourMenuOpen } from "@/lib/tourMenuOpen";
import { useSession } from "./SessionProvider";

/** How long to wait for a step's target before falling back to a centred card.
 *  Sized for the slow case: a step that navigates to a lazy-loaded route (the
 *  Dex, Settings) has to wait for the chunk to load and the page to paint
 *  before its target exists, which on a cold device load runs past a second. */
const TARGET_WAIT_MS = 3000;
/** Poll interval while waiting for a target to mount. */
const TARGET_POLL_MS = 60;
/** Gap between the spotlight ring and the copy card. */
const CARD_GAP_PX = 14;
/** Viewport margin the card is clamped inside. */
const EDGE_PX = 12;
/** Default ring padding when a step does not specify one. */
const DEFAULT_PADDING_PX = 8;

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * A target taller than this share of the viewport is framed from its top
 * edge whatever the step asked for: centring a page-tall section puts its
 * heading above the screen and leaves the card sitting on the part that is
 * left (the friends list, the My Pokémon list, the Settings section).
 */
const TALL_TARGET_VIEWPORT_SHARE = 0.55;
/** How many ancestors the visibility check walks for opacity / visibility. */
const VISIBILITY_WALK_DEPTH = 14;

/**
 * The selector for a step's spotlight: a full CSS selector when the step
 * names one, else the testid, which may itself come from device state
 * (the trainer's chosen bottom-bar slot).
 */
export function targetSelectorFor(step: TourStep): string | null {
  if (step.kind !== "spotlight") return null;
  if (step.targetSelector) return step.targetSelector;
  const testid = step.targetFrom
    ? (resolveWalkthroughTarget(step.targetFrom) ?? step.target)
    : step.target;
  return testid ? `[data-testid="${testid}"]` : null;
}

/**
 * Is this element something a ring around it would actually show?
 *
 * The engine used to accept any mounted node with a size, which rang three
 * kinds of nothing (2026-09-19 owner report): a swipe pane parked one
 * viewport to the side (the Trade Hub keeps visited panes mounted
 * off-stage, aria-hidden), a control faded to opacity 0 (the Dex page's
 * Next region button with no next region; the My Pokémon list mid-restore),
 * and a collapsed section. A hidden target is "not there yet": the poll
 * keeps waiting for it and the step degrades to a card at the deadline.
 */
export function isVisibleTarget(el: HTMLElement): boolean {
  if (!el.isConnected) return false;
  const r = el.getBoundingClientRect();
  if (r.width < 1 || r.height < 1) return false;
  if (el.closest('[aria-hidden="true"]')) return false;
  let node: HTMLElement | null = el;
  for (let depth = 0; node && depth < VISIBILITY_WALK_DEPTH; depth += 1) {
    const cs = window.getComputedStyle(node);
    if (cs.display === "none" || cs.visibility === "hidden" || cs.opacity === "0") return false;
    node = node.parentElement;
  }
  const vw = window.innerWidth || 0;
  if (vw > 0 && (r.right <= 0 || r.left >= vw)) return false;
  return true;
}

/** The first VISIBLE match for a selector, or null. */
export function findVisibleTarget(selector: string): HTMLElement | null {
  let nodes: NodeListOf<Element>;
  try {
    nodes = document.querySelectorAll(selector);
  } catch {
    return null;
  }
  for (const n of nodes) {
    if (n instanceof HTMLElement && isVisibleTarget(n)) return n;
  }
  return null;
}

/** Gap left between a target and whatever bar was covering it. */
const OVERLAY_CLEAR_PX = 10;
/** How many times the nudge re-checks (a header and a sticky row stack). */
const OVERLAY_NUDGE_ROUNDS = 3;

/**
 * Scroll a freshly framed target out from under fixed and sticky chrome.
 *
 * scrollIntoView({ block: "start" }) puts the target at y = 0, which on this
 * app is under the fixed header, and on the Dex page under the sticky
 * variant row as well: the ring was drawn around a search box nobody could
 * see (2026-09-19 owner report, "the spots are not covering the actual
 * areas"). This hit-tests the target's top edge with the tour's own overlay
 * made transparent to hit-testing, and when something else is on top,
 * scrolls the page by that bar's overlap. Repeats a few times because bars
 * stack. Pure DOM, no layout knowledge of any page.
 */
export function nudgeClearOfOverlays(el: HTMLElement, tourRoot: HTMLElement | null): void {
  for (let round = 0; round < OVERLAY_NUDGE_ROUNDS; round += 1) {
    const r = el.getBoundingClientRect();
    if (r.height < 1) return;
    const x = r.left + Math.min(r.width / 2, 40);
    const y = Math.max(r.top, 0) + 2;
    const prevPointer = tourRoot?.style.pointerEvents;
    if (tourRoot) tourRoot.style.pointerEvents = "none";
    let hit: Element | null = null;
    try {
      hit = document.elementFromPoint(x, y);
    } finally {
      if (tourRoot) tourRoot.style.pointerEvents = prevPointer ?? "";
    }
    if (!(hit instanceof HTMLElement) || hit === el || el.contains(hit) || hit.contains(el)) return;
    // The bar's own box, not the span inside it that took the hit: walk up
    // while the ancestor still ends at the same edge.
    let bar: HTMLElement = hit;
    while (bar.parentElement && bar.parentElement !== document.body) {
      const pr = bar.parentElement.getBoundingClientRect();
      if (Math.abs(pr.bottom - bar.getBoundingClientRect().bottom) > 2) break;
      bar = bar.parentElement;
    }
    const overlap = bar.getBoundingClientRect().bottom - r.top + OVERLAY_CLEAR_PX;
    if (overlap <= 0) return;
    const before = window.scrollY;
    window.scrollBy(0, -overlap);
    if (window.scrollY === before) return; // already at the top: nothing to give
  }
}

/** The hash path of a route, without its query, for "are we there" checks. */
function routePath(route: string): string {
  const q = route.indexOf("?");
  return q >= 0 ? route.slice(0, q) : route;
}

export function GuidedTour(): JSX.Element | null {
  const { tourOpen, tourScript, closeTour } = useSession();
  const [idx, setIdx] = useState(0);
  // The script: the getting-started tour, or one of the advanced walkthroughs
  // (lib/walkthroughs). A new script always starts from its first step.
  const steps = useMemo(() => stepsForScript(tourScript), [tourScript]);
  useEffect(() => {
    setIdx(0);
  }, [tourScript]);
  // null = no spotlight for this step (a `card` step, or a target that never
  // showed up). The card then renders centred.
  const [rect, setRect] = useState<Rect | null>(null);
  const targetElRef = useRef<HTMLElement | null>(null);
  /** The tour's own root, made transparent to hit-testing while the nudge
   *  looks for chrome covering a target. */
  const rootRef = useRef<HTMLDivElement | null>(null);
  /** Current step's target selector, so the rect tracker can re-find a node
   *  that was replaced by a remount. Held in a ref to keep `measure` stable. */
  const selectorRef = useRef<string | null>(null);

  // Resolve per-platform copy (iOS broadcast + draggable HUD vs Android screen
  // recording + fixed HUD). Memoised on idx so the activation effect's `step`
  // dependency stays referentially stable across renders; the platform is fixed
  // for the session.
  const rawStep: TourStep | undefined = steps[idx];
  const step: TourStep | undefined = useMemo(
    () => (rawStep ? stepForPlatform(rawStep, getPlatform()) : undefined),
    [rawStep],
  );
  const isFirst = idx === 0;
  const isLast = idx === steps.length - 1;

  /* ── Claim scroll ownership while the tour runs ──────────────────────────
     Each step navigates to a route and then scrolls its target into the middle
     of the viewport. That same route change wakes useScrollResetOnRoute, whose
     post-paint reset would otherwise snap the page back to the top after we
     scrolled — leaving the card floating over the top of the page instead of
     the control it describes (e.g. Settings' Dex visibility toggles). This flag
     tells that hook to stand down for as long as the tour is open. */
  useEffect(() => {
    setGuidedTourScrollActive(tourOpen);
    return () => setGuidedTourScrollActive(false);
  }, [tourOpen]);

  /* ── Open the main menu for the step that is about the main menu ────────
     Declared BEFORE the activation effect below so the panel is already
     mounting by the time that effect starts polling for it. The menu is the
     only target in the script that does not exist until something opens it, so
     the tour opens it and holds it: MainMenu ignores every dismissal while this
     flag is set, and the flag clears on step change, on close, and on unmount —
     which means no exit from this step can strand an open panel over the app. */
  useEffect(() => {
    setTourMenuOpen(Boolean(tourOpen && step?.openMenu));
    return () => setTourMenuOpen(false);
  }, [tourOpen, step]);

  /* ── Step activation: navigate, then find and frame the target ───────── */
  useEffect(() => {
    if (!tourOpen || !step) return;
    let cancelled = false;
    targetElRef.current = null;
    setRect(null);

    const route = step.route ?? (step.routeFrom ? resolveWalkthroughRoute(step.routeFrom) : undefined);
    // Compared on the path alone: hashGetSnapshot strips the query, so a
    // route carrying one used to re-navigate on every activation.
    if (route && hashGetSnapshot() !== routePath(route)) {
      hashNavigate(route);
    }

    const selector = targetSelectorFor(step);
    const hasTarget = selector != null;
    if (!hasTarget) {
      // A card step has no target; drop any selector the previous step left so
      // the tracker cannot re-frame a node this step never asked for.
      selectorRef.current = null;
    }
    if (!hasTarget && !step.press) return;

    const deadline = Date.now() + TARGET_WAIT_MS;
    if (selector) selectorRef.current = selector;
    // Two phases: tap the step's `press` control once it exists (a pane tab,
    // a mode switch), then find the target. A press that never mounts falls
    // through to the target search, which degrades like any other step.
    let pressed = !step.press;
    const pressSelector = step.press ? `[data-testid="${step.press}"]` : null;

    const poll = window.setInterval(() => {
      if (cancelled) return;
      if (!pressed && pressSelector) {
        // Already showing? Then the press would only undo it: a filter
        // menu toggle pressed on the way BACK through a walk closed the
        // panel the step is about, and a tab press is simply redundant.
        if (selector && findVisibleTarget(selector)) {
          pressed = true;
        } else {
          const btn = document.querySelector(pressSelector);
          if (btn instanceof HTMLElement) {
            pressed = true;
            btn.click();
            return;
          }
          if (Date.now() > deadline) pressed = true;
          return;
        }
      }
      if (!selector) {
        window.clearInterval(poll);
        return;
      }
      const el = findVisibleTarget(selector);
      if (el) {
        window.clearInterval(poll);
        targetElRef.current = el;
        // Centre it so the card always has room on one side. `auto` rather
        // than `smooth`: the rect tracker below reads the position on the
        // next frames, and a smooth scroll makes the ring chase the element
        // across the screen on slower devices. A page-tall target is framed
        // from its top edge instead, so its heading stays on screen.
        // Guarded: this runs inside an interval, and a throw here would leave
        // the poll running with no way to reach the next step.
        const tall =
          el.getBoundingClientRect().height >
          (window.innerHeight || 0) * TALL_TARGET_VIEWPORT_SHARE;
        el.scrollIntoView?.({
          block: tall ? "start" : (step.scrollBlock ?? "center"),
          inline: "nearest",
          behavior: "auto",
        });
        try {
          nudgeClearOfOverlays(el, rootRef.current);
        } catch {
          /* hit-testing is best effort; the ring still tracks the target */
        }
        measure();
      } else if (Date.now() > deadline) {
        // Target never mounted. Fall back to the centred card rather than
        // stalling the tour on a step the trainer cannot leave.
        window.clearInterval(poll);
      }
    }, TARGET_POLL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(poll);
    };
    // `measure` is stable (useCallback with no deps).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourOpen, idx, step]);

  /* ── Rect tracking ───────────────────────────────────────────────────── */
  const measure = useCallback(() => {
    let el = targetElRef.current;
    // RE-QUERY BEFORE GIVING UP. The node we framed can be REPLACED under us:
    // a step navigates, the destination route mounts, and a shared chrome
    // element like the tab bar is torn down and rebuilt. The old node is then
    // disconnected — same selector, different element — and because the poll
    // that found it has already stopped, the step loses its spotlight for the
    // rest of its life. That is what left the Raid step with no highlight
    // while Calendar, which happened not to lose the race, kept its own.
    if ((!el || !isVisibleTarget(el)) && selectorRef.current) {
      const found = findVisibleTarget(selectorRef.current);
      if (found) {
        el = found;
        targetElRef.current = found;
      }
    }
    // Not mounted, not laid out yet (a lazy route mid-suspense), faded out,
    // or parked off-stage: no spotlight until it is really on screen.
    if (!el || !isVisibleTarget(el)) {
      setRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, []);

  // Re-measure on anything that can move the target under us. Sticky bars,
  // the soft keyboard, and rotation all shift rects without a re-render.
  useEffect(() => {
    if (!tourOpen) return;
    const onChange = () => measure();
    window.addEventListener("scroll", onChange, true);
    window.addEventListener("resize", onChange);
    const raf = window.setInterval(onChange, 250);
    return () => {
      window.removeEventListener("scroll", onChange, true);
      window.removeEventListener("resize", onChange);
      window.clearInterval(raf);
    };
  }, [tourOpen, measure]);

  useLayoutEffect(() => {
    if (tourOpen) measure();
  }, [tourOpen, idx, measure]);

  /* ── Navigation ──────────────────────────────────────────────────────── */
  const finish = useCallback(() => {
    setIdx(0);
    setRect(null);
    targetElRef.current = null;
    closeTour();
  }, [closeTour]);

  const next = useCallback(() => {
    if (isLast) finish();
    else setIdx((i) => Math.min(i + 1, steps.length - 1));
  }, [isLast, finish, steps.length]);

  const prev = useCallback(() => setIdx((i) => Math.max(i - 1, 0)), []);

  // Arrow keys and Escape, for the web build and for anyone on a keyboard.
  useEffect(() => {
    if (!tourOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") finish();
      else if (e.key === "ArrowRight" || e.key === "Enter") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tourOpen, finish, next, prev]);

  if (!tourOpen || !step) return null;

  const pad = step.padding ?? DEFAULT_PADDING_PX;
  const ring = rect
    ? {
        top: rect.top - pad,
        left: rect.left - pad,
        width: rect.width + pad * 2,
        height: rect.height + pad * 2,
      }
    : null;

  return (
    <div
      ref={rootRef}
      className="fixed inset-0 z-[96]"
      role="dialog"
      aria-modal="true"
      aria-label={`Walkthrough, step ${idx + 1} of ${steps.length}: ${step.title}`}
      data-testid="guided-tour"
      data-tour-step={step.key}
    >
      {/* Dim. When there is no spotlight this is a plain full-screen scrim;
          when there is one, the ring element below carries the dim via its
          huge box-shadow so the hole is exact. */}
      {!ring && (
        <div className="absolute inset-0" style={{ background: "hsl(0 0% 0% / 0.72)" }} aria-hidden />
      )}

      {ring && (
        <div
          aria-hidden
          data-testid="guided-tour-spotlight"
          className="absolute rounded-[18px] pointer-events-none transition-all [transition-duration:var(--duration-fast)]"
          style={{
            top: ring.top,
            left: ring.left,
            width: ring.width,
            height: ring.height,
            // One shadow paints the whole dim; the element's own box is the hole.
            boxShadow:
              "0 0 0 9999px hsl(0 0% 0% / 0.72), 0 0 0 2px var(--accent-fill), 0 0 22px 2px var(--accent-fill-glow)",
          }}
        />
      )}

      {/* Full-screen tap catcher, so a stray tap on the app underneath cannot
          put the app in a state the next step does not expect. Sits under the
          card. Tapping the dim advances, which is the gesture people try; a
          tap INSIDE the ring is someone trying the control, and skipping the
          step under their finger taught nothing (2026-09-19), so it is
          swallowed instead. */}
      <button
        type="button"
        aria-hidden
        tabIndex={-1}
        onClick={(e) => {
          if (
            ring &&
            e.clientX >= ring.left &&
            e.clientX <= ring.left + ring.width &&
            e.clientY >= ring.top &&
            e.clientY <= ring.top + ring.height
          ) {
            return;
          }
          next();
        }}
        data-testid="guided-tour-scrim"
        className="absolute inset-0 w-full h-full cursor-default"
        style={{ background: "transparent" }}
      />

      <TourCard
        step={step}
        idx={idx}
        total={steps.length}
        lastLabel={tourScript === "intro" ? "Start grinding" : "Done"}
        ring={ring}
        isFirst={isFirst}
        isLast={isLast}
        onNext={next}
        onPrev={prev}
        onSkip={finish}
      />
    </div>
  );
}

/* ─── Card ────────────────────────────────────────────────────────────────
   Placed in whichever half of the viewport the ring is NOT in. With no ring
   it is vertically centred. The card is width-capped and clamped inside the
   viewport, so it never rides off a narrow screen or under the notch. */
function TourCard({
  step,
  idx,
  total,
  ring,
  isFirst,
  isLast,
  lastLabel,
  onNext,
  onPrev,
  onSkip,
}: {
  step: TourStep;
  idx: number;
  total: number;
  ring: Rect | null;
  isFirst: boolean;
  isLast: boolean;
  lastLabel: string;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
}) {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [placement, setPlacement] = useState<{ top: number } | null>(null);

  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    if (!ring) {
      setPlacement(null);
      return;
    }
    const vh = window.innerHeight;
    const h = el.offsetHeight;
    const below = ring.top + ring.height + CARD_GAP_PX;
    const above = ring.top - CARD_GAP_PX - h;
    // Prefer below; use above when below would overflow and above fits.
    // Neither fits when the ring is taller than the room left: then the card
    // covers whichever end of the ring is off screen anyway. A ring framed
    // from its top edge (its heading in view) gets the card at the bottom;
    // one whose top is already above the viewport gets it at the top.
    let top: number;
    if (below + h + EDGE_PX <= vh) top = below;
    else if (above >= EDGE_PX) top = above;
    else top = ring.top >= EDGE_PX ? vh - h - EDGE_PX : EDGE_PX;
    top = Math.max(EDGE_PX, Math.min(top, vh - h - EDGE_PX));
    setPlacement({ top });
  }, [ring, idx]);

  const centred = !ring || !placement;

  return (
    /* Positioning and animation are deliberately on DIFFERENT elements. The
       shared `anim-fade-up` keyframes animate `transform`, and a running or
       filled animation beats an inline style, which knocked the centred card
       half a screen to the right. The outer element owns placement; the inner
       one owns the entrance. */
    <div
      ref={cardRef}
      data-testid="guided-tour-card"
      className="absolute w-[min(92vw,26rem)]"
      style={{
        top: centred ? "50%" : placement.top,
        left: "50%",
        transform: centred ? "translate(-50%, -50%)" : "translateX(-50%)",
      }}
    >
    <div
      className="rounded-[var(--radius-xl)] p-5 anim-fade-up"
      style={{
        background: "var(--surface-raised)",
        border: "1px solid var(--accent-fill-glow)",
        boxShadow: "var(--shadow-lg)",
        maxHeight: "80dvh",
        overflowY: "auto",
      }}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <p
          className="text-[10px] uppercase tracking-[0.2em] font-bold"
          style={{ color: "var(--brand-green-on-light)" }}
          data-testid="guided-tour-eyebrow"
        >
          {step.eyebrow}
        </p>
        <button
          type="button"
          onClick={onSkip}
          aria-label="Close walkthrough"
          data-testid="guided-tour-skip"
          className="shrink-0 -mt-1 -mr-1 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full"
          style={{ background: "var(--surface-sunken)", color: "var(--text-secondary)" }}
        >
          Skip <X className="w-3 h-3" />
        </button>
      </div>

      <h2
        className="text-[19px] font-extrabold leading-tight"
        style={{ color: "var(--text-primary)" }}
        data-testid="guided-tour-title"
      >
        {step.title}
      </h2>

      <p
        className="mt-2 text-[13px] font-medium leading-relaxed"
        style={{ color: "var(--text-secondary)" }}
        data-testid="guided-tour-body"
      >
        {step.body}
      </p>

      {step.bullets && step.bullets.length > 0 && (
        <ul className="mt-3 space-y-1.5" data-testid="guided-tour-bullets">
          {step.bullets.map((b) => (
            <li
              key={b}
              className="flex items-start gap-2 text-[12px] font-semibold leading-snug"
              style={{ color: "var(--text-secondary)" }}
            >
              <Check
                className="w-3.5 h-3.5 mt-[1px] shrink-0"
                style={{ color: "var(--accent-fill)" }}
                aria-hidden
              />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Progress. A dot per step is too many at this length, so this is a
          bar plus a count. */}
      <div className="mt-4 flex items-center gap-3">
        <div
          className="flex-1 h-1 rounded-full overflow-hidden"
          style={{ background: "var(--surface-sunken)" }}
          aria-hidden
        >
          <div
            className="h-full rounded-full transition-all"
            style={{
              width: `${((idx + 1) / total) * 100}%`,
              background: "var(--accent-fill)",
            }}
          />
        </div>
        <span
          className="text-[10px] font-bold tabular-nums"
          style={{ color: "var(--text-tertiary)" }}
          data-testid="guided-tour-progress"
        >
          {idx + 1}/{total}
        </span>
      </div>

      <div className="mt-3 flex items-center gap-2">
        {!isFirst && (
          <button
            type="button"
            onClick={onPrev}
            data-testid="guided-tour-prev"
            className="h-11 px-4 rounded-full text-sm font-semibold inline-flex items-center gap-1"
            style={{
              background: "var(--surface-sunken)",
              border: "1px solid var(--border-subtle)",
              color: "var(--text-secondary)",
            }}
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
        )}
        <button
          type="button"
          onClick={onNext}
          data-testid="guided-tour-next"
          className="v2-neon-cta flex-1 h-11 rounded-full font-semibold text-sm inline-flex items-center justify-center gap-2"
        >
          {isLast ? lastLabel : "Next"} <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
    </div>
  );
}
